import React, { useState } from "react";
import { LogOut, Database, Globe, Palette, HelpCircle } from "lucide-react";
import { useTranslation } from "react-i18next";
import i18n from "../i18n";
import { User } from "../types/Types";

interface NavbarMenuProps {
  user: User | null;
  onLogout: () => void;
  queryInspectorEnabled: boolean;
  onToggleQueryInspector: () => void;
  onOpenTheme: () => void;
  onOpenHelp: () => void;
}

const ROW =
  "flex w-full items-center gap-3 px-4 py-2.5 text-left text-sm text-gray-700 transition-colors hover:bg-blue-50";

const NavbarMenu: React.FC<NavbarMenuProps> = ({
  user,
  onLogout,
  queryInspectorEnabled,
  onToggleQueryInspector,
  onOpenTheme,
  onOpenHelp,
}) => {
  const { t } = useTranslation();
  const [currentLang, setCurrentLang] = useState((i18n.language || "mk").split("-")[0]);
  const fullName = [user?.firstName, user?.lastName].filter(Boolean).join(" ");

  const handleLangChange = (lng: "mk" | "en") => {
    i18n.changeLanguage(lng);
    setCurrentLang(lng);
  };

  const langButton = (lng: "mk" | "en", label: string) => (
    <button
      type="button"
      onClick={() => handleLangChange(lng)}
      className={`px-2 py-0.5 text-xs ${lng === "en" ? "border-l border-gray-200" : ""} ${
        currentLang === lng ? "bg-blue-500 font-semibold text-white" : "text-gray-600 hover:bg-blue-50"
      }`}
    >
      {label}
    </button>
  );

  return (
    <div className="absolute right-0 mt-2 w-64 overflow-hidden rounded-lg border border-gray-200 bg-white text-gray-700 shadow-lg">
      <div className="border-b border-gray-100 bg-gray-50 px-4 py-3">
        <div className="truncate text-base font-semibold text-gray-900">
          {fullName || user?.userName}
        </div>
        {fullName && (
          <div className="truncate text-sm text-gray-500">
            {user?.userName}
          </div>
        )}
        {user?.role && (
          <div className="mt-1 text-sm capitalize text-blue-600">
            {user.role}
          </div>
        )}
      </div>

      <div className="py-1">
        <button
          type="button"
          onClick={onToggleQueryInspector}
          className={`${ROW} ${queryInspectorEnabled ? "bg-blue-50 text-blue-700" : ""}`}
        >
          <Database size={18} className="shrink-0 text-blue-500" />
          <span className="min-w-0 flex-1 truncate">{t("navbar.queryInspector", "Database")}</span>
        </button>

        <div className={ROW}>
          <Globe size={18} className="shrink-0 text-blue-500" />
          <span className="min-w-0 flex-1 truncate">{t("common.language")}</span>
          <div className="flex overflow-hidden rounded-md border border-gray-200">
            {langButton("mk", "MK")}
            {langButton("en", "EN")}
          </div>
        </div>

        <button type="button" onClick={onOpenTheme} className={ROW}>
          <Palette size={18} className="shrink-0 text-blue-500" />
          <span className="min-w-0 flex-1 truncate">{t("sidebarFooter.theme")}</span>
        </button>

        <button type="button" onClick={onOpenHelp} className={ROW}>
          <HelpCircle size={18} className="shrink-0 text-blue-500" />
          <span className="min-w-0 flex-1 truncate">{t("sidebarFooter.help")}</span>
        </button>
      </div>

      <div className="border-t border-gray-100 py-1">
        <button type="button" onClick={onLogout} className={ROW}>
          <LogOut size={18} className="shrink-0 text-gray-500" />
          <span>{t("common.logout")}</span>
        </button>
      </div>
    </div>
  );
};

export default NavbarMenu;
